import { type CSSProperties, type FunctionComponent } from 'react';
import cn from 'classnames';
import { Cell, HStack, IconArrowDown, IconDrag, Stub, StubCell, useVStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { type TreeSize } from './lib/types';
import styles from './TreeStub.module.css';

export interface TreeStubProps extends TestProps {
    grouped?: boolean;
    isEditor?: boolean;
    isFlat?: boolean;
    rowsCount?: number;
    size?: TreeSize;
    width?: CSSProperties['width'];
}

const renderEditorCellStub = (size: TreeSize) => (
    <button
        type="button"
        tabIndex={-1}
        aria-hidden
        className={cn(styles.editorCell, {
            [styles.editorCellSizeM]: size === 'm',
            [styles.editorCellSizeS]: size === 's',
        })}
    >
        <IconDrag size="20" color="gray-disabled" />
    </button>
);

const renderStubs = (count: number, options: { isEditor: boolean; isFlat: boolean; size: TreeSize }) => {
    const { isEditor, isFlat, size } = options;

    return Array.from({ length: count }).map((_: unknown, index: number) => {
        if (index === 0 && !isFlat) {
            return (
                <HStack key={index} className={styles.firstLevel}>
                    {isEditor && renderEditorCellStub(size)}
                    <Cell size={size} isGrowable={false}>
                        <Stub height={15} width={20} borderRadius="8" />
                    </Cell>
                    <StubCell size={size} leftPadding={false} isGrowable />
                </HStack>
            );
        }

        return (
            <HStack key={index} className={isFlat ? styles.firstLevel : undefined}>
                {isEditor && renderEditorCellStub(size)}
                {!isFlat && (
                    <Cell size={size} isGrowable={false}>
                        <Stub height={0} width={30} />
                    </Cell>
                )}
                <StubCell size={size} leftPadding={isFlat} isGrowable />
            </HStack>
        );
    });
};

const renderGroupedStubs = (count: number, options: { isEditor: boolean; size: TreeSize }) => {
    const { isEditor, size } = options;

    return Array.from({ length: count }).map((_: unknown, index: number) => (
        <div className={styles.group} key={index}>
            <HStack>
                <HStack className={styles.selectionCell} justifyContent="flex-end" alignItems="center" />
                {isEditor && renderEditorCellStub(size)}
                <Cell size={size} isGrowable={false} rightPadding={false}>
                    <IconArrowDown className={styles.groupExpander} size="20" color="gray-disabled" />
                </Cell>
                <StubCell size={size} leftPadding={false} isGrowable />
                <Cell size={size} isGrowable={false}>
                    <Stub width={20} height={20} borderRadius="round" />
                </Cell>
            </HStack>
            <HStack>
                <div
                    className={cn(styles.nestedLeadingSpace, {
                        [styles.nestedLeadingSpaceEditor]: isEditor,
                    })}
                />
                <div
                    className={cn(styles.levelConnector, {
                        [styles.levelConnectorSizeM]: size === 'm',
                    })}
                />
                <StubCell size={size} leftPadding={false} isGrowable />
                <Cell size={size} isGrowable={false}>
                    <Stub width={20} height={20} borderRadius="round" />
                </Cell>
            </HStack>
        </div>
    ));
};

export const TreeStub: FunctionComponent<TreeStubProps> = ({
    'data-testid': dataTestId = 'TreeStub',
    grouped = false,
    isEditor = false,
    isFlat = false,
    rowsCount,
    size = 's',
    width,
}) => {
    const className = useVStack({});

    const options = { isEditor, isFlat, size };
    const count = rowsCount ?? (grouped ? 10 : 20);

    return (
        <div className={className} data-testid={dataTestId} style={{ width }}>
            {grouped ? (
                renderGroupedStubs(count, { isEditor, size })
            ) : (
                <>
                    {!isFlat && (
                        <HStack>
                            {isEditor && renderEditorCellStub(size)}
                            <StubCell size={size} isGrowable />
                        </HStack>
                    )}
                    {renderStubs(count, options)}
                </>
            )}
        </div>
    );
};
